import { useAuthenticator } from '@aws-amplify/ui-react-native';
import { useResetApp } from '../components/ResetAppProvider';
import { useCallback, useState } from 'react';
import { waitForUserLogout } from './useIsUserLoggedIn';
import { useResetUrlEmailState } from './useUrlAppParams';
import useUserDevicePushTokens from './api/useUserDevicePushTokens';
import { flushAnalyticsEvents } from '../utils/analytics/queue';
import { recordSessionStop } from '../utils/analytics/record';

export type UseSignOutAndResetAppReturnValue = {
    signOutAndResetApp: () => void;
    isResetting: boolean;
};

/** Longest sign-out waits on the final analytics send, so a stalled network cannot hold the user in the app. */
const ANALYTICS_FLUSH_TIMEOUT_MS = 2000;

/**
 * Close the analytics session and send what is queued while the user's credentials can still sign the
 * request. Sign-out clears them before the app unmounts, so leaving this to the unmount cleanup loses it.
 */
const flushAnalyticsBeforeSignOut = () => {
    recordSessionStop();
    return Promise.race([
        flushAnalyticsEvents(),
        new Promise((resolve) => setTimeout(resolve, ANALYTICS_FLUSH_TIMEOUT_MS)),
    ]);
};

// retrieves only the current value of 'user' from 'useAuthenticator'
// @ts-expect-error TS7006
const userSelector = (context) => [context.user];

export const useSignOutAndResetApp = () => {
    const { deleteDevicePushToken } = useUserDevicePushTokens();
    const { resetUrlEmailState } = useResetUrlEmailState();
    const [isResetting, setIsResetting] = useState(false);
    // delay the reset a bit.  If it happens too quickly, amplify auth immediately sends the user to the login
    const { signOut } = useAuthenticator(userSelector);
    const resetApp = useResetApp();

    const signOutAndResetApp = useCallback(() => {
        (async () => {
            setIsResetting(true);
            await Promise.all([deleteDevicePushToken(), flushAnalyticsBeforeSignOut()]);
            signOut();
            resetUrlEmailState();
            await waitForUserLogout();
            resetApp();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signOut, resetUrlEmailState, resetApp]);

    return {
        signOutAndResetApp,
        isResetting,
    };
};
